import {
  getSkuById,
  PRIMARY_SERVICE_CATEGORIES,
  type BillingType,
  type BillingInterval,
  type ServiceCategory,
} from '../pricebook/pricebook';

const BUNDLE_DISCOUNT_PERCENT = 15;
const BUNDLE_DISCOUNT_LABEL =
  '15% bundle discount on recurring primary services';

export interface CartItemInput {
  readonly skuId: string;
  readonly quantity: number;
}

export interface CalculatedOrderLineItem {
  readonly skuId: string;
  readonly name: string;
  readonly billingType: BillingType;
  readonly interval?: BillingInterval;
  readonly unitAmountCents: number;
  readonly quantity: number;
  readonly lineSubtotalCents: number;
  readonly isPrimaryService: boolean;
  readonly serviceCategory: ServiceCategory;
}

export interface CalculatedOrder {
  readonly lineItems: readonly CalculatedOrderLineItem[];
  readonly subtotalRecurringCents: number;
  readonly subtotalOneTimeCents: number;
  readonly discountCents: number;
  readonly discountReason: string | null;
  readonly discountBreakdown: readonly {
    readonly code: 'bundle_primary_services';
    readonly label: string;
    readonly amountCents: number;
  }[];
  readonly totalDueNowCents: number;
  readonly recurringMonthlyCents: number;
}

export interface CalcOrderOptions {
  readonly promoCode?: string | null;
  readonly existingPrimaryCategories?: readonly ServiceCategory[];
}

function normalizeCartItems(cartItems: readonly CartItemInput[]): Map<string, number> {
  if (!Array.isArray(cartItems)) {
    throw new Error('cartItems must be an array');
  }

  const merged = new Map<string, number>();
  for (const item of cartItems) {
    const skuId = typeof item.skuId === 'string' ? item.skuId.trim() : '';
    if (!skuId) throw new Error('Each cart item must include a skuId');

    const quantity = Number(item.quantity);
    if (!Number.isInteger(quantity) || quantity <= 0) {
      throw new Error(
        `Invalid quantity for sku "${skuId}". Quantity must be a positive integer.`,
      );
    }
    merged.set(skuId, (merged.get(skuId) ?? 0) + quantity);
  }

  return merged;
}

export function roundPercentCents(amountCents: number, percent: number): number {
  return Math.round((amountCents * percent) / 100);
}

export function calcOrder(
  cartItems: readonly CartItemInput[],
  options: CalcOrderOptions = {},
): CalculatedOrder {
  // Reserved for future use (promo engine can branch on this).
  void options.promoCode;

  const merged = normalizeCartItems(cartItems);
  const lineItems: CalculatedOrderLineItem[] = [];

  let subtotalRecurringCents = 0;
  let subtotalOneTimeCents = 0;
  let recurringPrimarySubtotalCents = 0;
  const recurringPrimaryCategories = new Set<ServiceCategory>();
  const existingPrimaryCategories = new Set<ServiceCategory>(
    Array.isArray(options.existingPrimaryCategories)
      ? options.existingPrimaryCategories.filter((value): value is ServiceCategory =>
          PRIMARY_SERVICE_CATEGORIES.includes(
            value as (typeof PRIMARY_SERVICE_CATEGORIES)[number],
          ),
        )
      : [],
  );

  merged.forEach((quantity, skuId) => {
    const sku = getSkuById(skuId);
    if (!sku) {
      throw new Error(`Unknown skuId "${skuId}"`);
    }

    const lineSubtotalCents = sku.amountCents * quantity;
    const lineItem: CalculatedOrderLineItem = {
      skuId: sku.skuId,
      name: sku.name,
      billingType: sku.billingType,
      interval: sku.interval,
      unitAmountCents: sku.amountCents,
      quantity,
      lineSubtotalCents,
      isPrimaryService: sku.isPrimaryService,
      serviceCategory: sku.serviceCategory,
    };
    lineItems.push(lineItem);

    if (sku.billingType === 'recurring') {
      subtotalRecurringCents += lineSubtotalCents;
      const isDiscountEligiblePrimary =
        sku.isPrimaryService &&
        PRIMARY_SERVICE_CATEGORIES.includes(
          sku.serviceCategory as (typeof PRIMARY_SERVICE_CATEGORIES)[number],
        );
      if (isDiscountEligiblePrimary) {
        recurringPrimarySubtotalCents += lineSubtotalCents;
        recurringPrimaryCategories.add(sku.serviceCategory);
      }
    } else {
      subtotalOneTimeCents += lineSubtotalCents;
    }
  });

  lineItems.sort((a, b) => a.skuId.localeCompare(b.skuId));

  const resultingPrimaryCategories = new Set<ServiceCategory>(existingPrimaryCategories);
  recurringPrimaryCategories.forEach((category) => resultingPrimaryCategories.add(category));

  const hasBundleDiscount =
    recurringPrimarySubtotalCents > 0 && resultingPrimaryCategories.size >= 2;
  const discountCents = hasBundleDiscount
    ? roundPercentCents(recurringPrimarySubtotalCents, BUNDLE_DISCOUNT_PERCENT)
    : 0;
  const discountReason = hasBundleDiscount ? BUNDLE_DISCOUNT_LABEL : null;
  const discountBreakdown = hasBundleDiscount
    ? [
        {
          code: 'bundle_primary_services' as const,
          label: BUNDLE_DISCOUNT_LABEL,
          amountCents: discountCents,
        },
      ]
    : [];

  const recurringMonthlyCents = Math.max(0, subtotalRecurringCents - discountCents);
  const totalDueNowCents = recurringMonthlyCents + subtotalOneTimeCents;

  return {
    lineItems,
    subtotalRecurringCents,
    subtotalOneTimeCents,
    discountCents,
    discountReason,
    discountBreakdown,
    totalDueNowCents,
    recurringMonthlyCents,
  };
}
